// dSpr Review Management - `/dSpr_ReviewManagement`
//
// EMPLOYEE-grouped per-cycle review view. One accordion card per employee.
// Click the card → expanded section shows the reporting manager + past-action
// chips + a cascade button stack (only the next eligible action is enabled).
//
// Hard rules (per spec):
//   • NO bulk-send features. Every send button targets ONE employee.
//   • NO manager dropdown filter.
//   • Single status filter at a time (the 6 stat cards are the filter UI).
//
// Sibling page: dSpr_CycleScheduler (manager-grouped view + bulk sends).
// Backend:      /api/dspr/cycles + /api/dspr/reviews
import React, { useEffect, useMemo, useState } from "react";
// Swal removed - themed alerts via useDsprAlerts() (see utils/dSprAlerts.js).
import Select from "react-select";
import { useNavigate, useLocation } from "react-router-dom";
import { FaRegEye } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import { profileColorFromName } from "./utils/profileColor";
import { useDsprAlerts } from "./utils/dSprAlerts";
import { fmtDate, fmtDateTimeIST, fmtDateTimeShort } from "./utils/dSpr_DateFormat";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";

// Per-status pill colours (employee chip + card header). Same set as
// Cycle Scheduler so the two pages read consistently.
const STATUS_COLORS = {
    "Not Started": { bg: "#f1f5f9", fg: "#475569" },
    "Initiated":   { bg: "#eef2ff", fg: "#4338ca" },
    "In Progress": { bg: "#fef3c7", fg: "#92400e" },
    "Submitted":   { bg: "#d1fae5", fg: "#065f46" },
    "Approved":    { bg: "#dcfce7", fg: "#14532d" },
};

// Per-cycle status pill (LIVE / SCHEDULED / CLOSED)
const CYCLE_STATUS_PILL = {
    Active:    { bg: "#dcfce7", fg: "#065f46", label: "LIVE" },
    Scheduled: { bg: "#eef2ff", fg: "#4338ca", label: "SCHEDULED" },
    Closed:    { bg: "#f1f5f9", fg: "#475569", label: "CLOSED" },
};

// email_type → display label (for chips + log entries)
const EMAIL_TYPE_LABEL = {
    request:      "Requested",
    reminder1:    "Reminder 1",
    reminder2:    "Reminder 2",
    confirmation: "Confirmation",
};

// 6 stat filter cards. The key matches dspr_reviews.status (or null for Total).
const STAT_CARDS = [
    { key: null,           label: "Total reviews", colorClass: "dspr-rm-stat-blue" },
    { key: "Not Started",  label: "Not started",   colorClass: "dspr-rm-stat-grey" },
    { key: "Initiated",    label: "Initiated",     colorClass: "dspr-rm-stat-indigo" },
    { key: "In Progress",  label: "In progress",   colorClass: "dspr-rm-stat-amber" },
    { key: "Submitted",    label: "Submitted",     colorClass: "dspr-rm-stat-blue2" },
    { key: "Approved",     label: "Approved",      colorClass: "dspr-rm-stat-green" },
];

// ── Helpers ─────────────────────────────────────────────────────────────────
function safeArr(maybe) {
    if (Array.isArray(maybe)) return maybe;
    if (maybe && Array.isArray(maybe.rows)) return maybe.rows;
    return [];
}
// Formatters live in src/utils/dSpr_DateFormat.js - imported above.

// Action label (for confirmation dialog + preview modal header). The button
// text itself is short ("Send Reminder 1") - set inline in EmployeeReviewCard
// to match the Cycle Scheduler manager-block convention.
function actionLabel(kind) {
    return {
        request:   "Request Review",
        reminder1: "Reminder 1",
        reminder2: "Reminder 2",
    }[kind] || kind;
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
function DSprReviewManagement({ navSize }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert, showConfirm, AlertsHost } = useDsprAlerts();

    const [cycles, setCycles] = useState([]);
    const [selectedCycle, setSelectedCycle] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState(null);   // null = all
    const [search, setSearch] = useState("");

    // Accordion state
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Email Log modal - set to the review whose log should be shown
    const [logModalReview, setLogModalReview] = useState(null);

    // Preview modal (reuses BatchPreviewModal from Cycle Scheduler - see export
    // at the bottom of this file. The single-emp variant passes
    // batch.singleReviewId + batch.singleEmployeeName so the Send button text
    // and the post-confirm callback know they're single-mode).
    const [previewBatch, setPreviewBatch] = useState(null);

    // ── Auth + initial load ──────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/employee/logined_employee", { credentials: "include" });
                const data = await res.json();
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login"); return;
                }
                const me = Array.isArray(data) ? data[0] : data;
                if (!me?.emp_id) { navigate("/login"); return; }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            } catch { navigate("/login"); return; }
            await fetchCycles();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCycles = async () => {
        try {
            const res = await apiFetch("/api/dspr/cycles", { credentials: "include" });
            const list = await res.json();
            const cs = Array.isArray(list) ? list : [];
            setCycles(cs);
            // Default-select: ?cycle= query param > Active cycle > newest cycle
            const params = new URLSearchParams(location.search);
            const qid = params.get("cycle");
            const q = params.get("q") || "";
            if (q) setSearch(q);
            const target = (qid && cs.find(c => c.id === qid))
                || cs.find(c => c.status === "Active")
                || cs[0]
                || null;
            if (target) setSelectedCycle(target);
            else setLoading(false);
        } catch (e) {
            showAlert("error", "Couldn't load cycles", e.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedCycle) return;
        fetchReviews(selectedCycle.id);
        // Reset accordion + filter when cycle changes
        setExpandedRows(new Set());
        setStatusFilter(null);
        setLogModalReview(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCycle?.id]);

    const fetchReviews = async (cycleId) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/api/dspr/reviews/by-cycle/${cycleId}`, { credentials: "include" });
            const list = await res.json();
            setReviews(safeArr(list));
        } catch (e) {
            showAlert("error", "Couldn't load reviews", e.message);
            setReviews([]);
        } finally {
            setLoading(false);
        }
    };

    // ── Derived stats (6 cards) ──────────────────────────────────────────────
    const stats = useMemo(() => {
        const acc = { total: reviews.length, "Not Started": 0, "Initiated": 0, "In Progress": 0, "Submitted": 0, "Approved": 0 };
        reviews.forEach(r => { if (acc[r.status] !== undefined) acc[r.status]++; });
        return acc;
    }, [reviews]);

    // ── Search + status filter ───────────────────────────────────────────────
    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return reviews
            .filter(r => !statusFilter || r.status === statusFilter)
            .filter(r => {
                if (!q) return true;
                const blob = [
                    r.employee_id, r.emp_first_name, r.emp_last_name, r.emp_mail_id,
                    r.job_position, r.manager_id, r.manager_first_name, r.manager_last_name,
                ].filter(Boolean).join(" ").toLowerCase();
                return blob.includes(q);
            });
    }, [reviews, statusFilter, search]);

    const submittedCount = useMemo(
        () => reviews.filter(r => r.status === "Submitted" || r.status === "Approved").length,
        [reviews]
    );
    const completePct = reviews.length > 0 ? Math.round((submittedCount / reviews.length) * 100) : 0;

    // ── Expand All toggle ────────────────────────────────────────────────────
    const allExpanded = visibleRows.length > 0 && visibleRows.every(r => expandedRows.has(r.id));
    const toggleExpandAll = () => {
        if (allExpanded) {
            setExpandedRows(new Set());
        } else {
            setExpandedRows(new Set(visibleRows.map(r => r.id)));
        }
    };
    const toggleRow = (reviewId) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(reviewId)) next.delete(reviewId);
            else next.add(reviewId);
            return next;
        });
    };

    // ── Send actions ─────────────────────────────────────────────────────────
    const doSendSingle = async (review, kind) => {
        const fullName = `${review.emp_first_name || ""} ${review.emp_last_name || ""}`.trim();
        const num = kind === "request" ? null : parseInt(kind.replace("reminder", ""), 10);
        const url = kind === "request"
            ? `/api/dspr/reviews/${encodeURIComponent(review.id)}/send-request`
            : `/api/dspr/reviews/${encodeURIComponent(review.id)}/send-reminder/${num}`;
        setBusy(`Sending ${actionLabel(kind)}…`);
        try {
            const res = await apiFetch(url, { method: "POST", credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Send failed");
            await fetchReviews(selectedCycle.id);
            showAlert("success", `Sent to ${fullName}`);
            return data;
        } catch (e) {
            showAlert("error", "Send failed", e.message);
            throw e;
        } finally {
            setBusy(null);
        }
    };

    const confirmAndSendSingle = async (review, kind) => {
        const fullName = `${review.emp_first_name || ""} ${review.emp_last_name || ""}`.trim();
        const ok = await showConfirm({
            title: `Send ${actionLabel(kind)} to ${fullName}?`,
            body: "This will fire one email.",
            confirmLabel: "Send",
        });
        if (!ok) return;
        try { await doSendSingle(review, kind); } catch { /* already toasted */ }
    };

    // ── Bulk Request Review (cycle-wide) ────────────────────────────────────
    // Fires a Request email to every Not-Started review in this cycle. Each
    // email is still a separate per-employee send - this just batches the
    // confirm + dispatch loop server-side.
    const confirmBulkRequest = async () => {
        if (!selectedCycle) return;
        if (selectedCycle.status !== "Active") {
            showAlert("warning", "Cycle not Active", "Activate the cycle on the Cycle Scheduler page first.");
            return;
        }
        const notStartedCount = reviews.filter(r => r.status === "Not Started").length;
        if (notStartedCount === 0) {
            showAlert("info", "Nothing to request", "Every employee has already received an initial Request email.");
            return;
        }
        const ok = await showConfirm({
            title: "Send Bulk Request Review?",
            body: `This will fire ${notStartedCount} separate emails - one to each employee with status Not Started. Each email is personalized with the recipient's own self-review link.`,
            confirmLabel: "Send Bulk",
        });
        if (!ok) return;
        setBusy(`Sending bulk request to ${notStartedCount}…`);
        try {
            const res = await apiFetch(`/api/dspr/reviews/bulk/${selectedCycle.id}/request`, {
                method: "POST", credentials: "include",
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Bulk send failed");
            await fetchReviews(selectedCycle.id);
            const failureLine = data.failures && data.failures.length
                ? `${data.failures.length} skipped: ${data.failures.slice(0, 3).map(f => f.name).join(", ")}${data.failures.length > 3 ? ` (+${data.failures.length - 3} more)` : ""}`
                : `${data.sent} Request email${data.sent !== 1 ? "s" : ""} delivered.`;
            showAlert(
                data.sent === data.attempted ? "success" : "warning",
                `${data.sent} / ${data.attempted} sent`,
                failureLine,
            );
        } catch (e) {
            showAlert("error", "Bulk send failed", e.message);
        } finally {
            setBusy(null);
        }
    };

    // ── Preview modal trigger (single-emp) ───────────────────────────────────
    const openSinglePreview = (review, kind) => {
        setPreviewBatch({
            cycleId: selectedCycle.id,
            singleReviewId: review.id,
            singleEmployeeName: `${review.emp_first_name || ""} ${review.emp_last_name || ""}`.trim(),
            act: kind,
            label: actionLabel(kind),
            cycleHalfLabel: selectedCycle.half_label,
            cycleEndDate: selectedCycle.end_date,
            employees: [review], // 1-element array
        });
    };

    // Called by BatchPreviewModal when HR clicks "Send to X". Passed the whole
    // batch object; we branch on singleReviewId vs (legacy) managerId. The
    // Cycle Scheduler page binds the managerId path; this page binds the
    // single-review path.
    const onPreviewConfirmSend = async (batch) => {
        if (batch.singleReviewId) {
            const review = reviews.find(r => r.id === batch.singleReviewId);
            if (!review) throw new Error("Review row not found");
            await doSendSingle(review, batch.act);
        }
    };

    // ── Cycle dropdown options ───────────────────────────────────────────────
    // Sort year DESC, half_num DESC, then Active > Scheduled > Closed within slot.
    const cycleOptions = useMemo(() => {
        const order = { Active: 0, Scheduled: 1, Closed: 2 };
        return cycles
            .slice()
            .sort((a, b) => {
                if (a.year !== b.year) return b.year - a.year;
                if (a.half_num !== b.half_num) return b.half_num - a.half_num;
                return (order[a.status] ?? 9) - (order[b.status] ?? 9);
            })
            .map(c => ({ value: c.id, label: `${c.half_label} - ${c.status}` }));
    }, [cycles]);

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="dspr-container">
            {busy && (
                <div className="ec-loader-overlay">
                    <div className="ec-loader-spinner" />
                    <div className="ec-loader-text">{busy}</div>
                </div>
            )}
            <div className={`dspr-dashboard ${navSize}`}>
                {/* Themed alerts mounted INSIDE the dashboard so the banner
                    doesn't become a flex-row item beside the dashboard (which
                    caused a horizontal shift when shown). */}
                <AlertsHost />
                {/* ════════════════════════════════════════════════════════════
                    REGION 1 - Page header (title + subtitle only, no buttons)
                    ════════════════════════════════════════════════════════════ */}
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        SPR - Review Management
                    </h2>
                    <p className="dspr-subtitle">
                        Employee-first orchestration - track and manage per-employee reviews.
                    </p>
                </div>

                <div className="dspr-dashboard-content">
                    {/* ════════════════════════════════════════════════════════
                        REGION 2 - Cycle dropdown + LIVE/dates row
                        ════════════════════════════════════════════════════════ */}
                    <div className="dspr-rm-region dspr-rm-cycle-region">
                        <div className="dspr-rm-cycle-label">Cycle:</div>
                        <Select
                            options={cycleOptions}
                            value={selectedCycle ? { value: selectedCycle.id, label: `${selectedCycle.half_label} - ${selectedCycle.status}` } : null}
                            onChange={(opt) => {
                                const next = cycles.find(c => c.id === opt.value);
                                setSelectedCycle(next || null);
                            }}
                            isSearchable={false}
                            classNamePrefix="dspr-rm-cycle-select"
                            styles={{ container: (p) => ({ ...p, width: "100%" }) }}
                        />
                        {selectedCycle && (() => {
                            const sp = CYCLE_STATUS_PILL[selectedCycle.status] || CYCLE_STATUS_PILL.Closed;
                            const reminders = safeArr(selectedCycle.reminders);
                            return (
                                <div className="dspr-rm-meta-row">
                                    <span className="dspr-pill" style={{ background: sp.bg, color: sp.fg, fontWeight: 700, letterSpacing: 0.5 }}>● {sp.label}</span>
                                    <span className="dspr-rm-meta-chip"><strong>End Date:</strong> {selectedCycle.end_date}</span>
                                    {reminders.map(rm => (
                                        <span key={rm.reminder_number} className="dspr-rm-meta-chip">
                                            <strong>R{rm.reminder_number}:</strong> {rm.reminder_date}
                                            {rm.has_fired && <span className="dspr-reminder-fired-badge">sent</span>}
                                        </span>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>

                    {/* Bulk Request Review - fires Request to every Not-Started review.
                        Disabled when no Not-Started rows remain (i.e. every employee
                        already received the initial Request email - sending again would
                        be a duplicate, and the backend filter would no-op anyway). */}
                    {(() => {
                        const notStartedCount = reviews.filter(r => r.status === "Not Started").length;
                        const cycleActive = selectedCycle?.status === "Active";
                        const disabled = !selectedCycle || !cycleActive || notStartedCount === 0;
                        const tooltip =
                            !selectedCycle              ? "Select a cycle first"
                          : !cycleActive                ? "Activate the cycle first"
                          : notStartedCount === 0       ? "All employees have already received the Request email"
                          : `Send Request email to ${notStartedCount} Not-Started review${notStartedCount === 1 ? "" : "s"}`;
                        return (
                            <div className="dspr-rm-bulk-row">
                                <button type="button" className="dspr-rm-bulk-request"
                                    onClick={confirmBulkRequest}
                                    disabled={disabled}
                                    title={tooltip}>
                                    📧 Bulk Request Review{notStartedCount > 0 ? ` (${notStartedCount})` : ""}
                                </button>
                            </div>
                        );
                    })()}

                    {/* ════════════════════════════════════════════════════════
                        REGION 3 - 6 stat filter cards (clickable)
                        ════════════════════════════════════════════════════════ */}
                    {/* No dspr-rm-region wrapper here - drops the dashed border
                        + white card and lets the stat tiles sit clean on the
                        page background (matches the dEpr reviews-stats row). */}
                    <div className="dspr-rm-stats-grid">
                        {STAT_CARDS.map(card => {
                            const isActive = statusFilter === card.key;
                            const num = card.key === null ? stats.total : (stats[card.key] || 0);
                            return (
                                <div key={card.key || "all"}
                                    className={`dspr-rm-stat ${card.colorClass} ${isActive ? "active" : ""}`}
                                    role="button" tabIndex={0}
                                    onClick={() => setStatusFilter(isActive ? null : card.key)}>
                                    <div className="dspr-rm-stat-head">
                                        <span className="dspr-rm-stat-label">{card.label.toUpperCase()}</span>
                                        {card.key === null && statusFilter !== null && (
                                            <span className="dspr-rm-filtered-chip">● FILTERED</span>
                                        )}
                                    </div>
                                    <div className="dspr-rm-stat-num">{num}</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* ════════════════════════════════════════════════════════
                        REGION 4 - Search + Expand All
                        ════════════════════════════════════════════════════════ */}
                    <div className="dspr-rm-region dspr-rm-search-row">
                        <input
                            type="text"
                            className="dspr-rm-search"
                            placeholder="Search employee, ID, role, manager…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <div className="dspr-rm-search-actions">
                            <button type="button" className="dspr-rm-expand-toggle" onClick={toggleExpandAll} disabled={visibleRows.length === 0}>
                                {allExpanded ? "Collapse All" : "Expand All"}
                            </button>
                            <span className="dspr-rm-complete-pill">{completePct}% Complete</span>
                        </div>
                    </div>

                    {/* ════════════════════════════════════════════════════════
                        REGION 5/6 - Employee accordion cards
                        Each card collapses/expands independently.
                        ════════════════════════════════════════════════════════ */}
                    <div className="dspr-rm-region dspr-rm-cards-region">
                        {loading ? (
                            <div className="dspr-empty">Loading reviews…</div>
                        ) : !selectedCycle ? (
                            <div className="dspr-empty">Select a cycle above to begin.</div>
                        ) : visibleRows.length === 0 ? (
                            <div className="dspr-empty">No reviews match the current filters.</div>
                        ) : (
                            visibleRows.map(review => (
                                <EmployeeReviewCard
                                    key={review.id}
                                    review={review}
                                    cycle={selectedCycle}
                                    isExpanded={expandedRows.has(review.id)}
                                    onToggle={() => toggleRow(review.id)}
                                    onShowLog={() => setLogModalReview(review)}
                                    onSend={confirmAndSendSingle}
                                    onPreview={openSinglePreview}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>

            {previewBatch && (
                <BatchPreviewModal
                    batch={previewBatch}
                    onClose={() => setPreviewBatch(null)}
                    onDirectSend={onPreviewConfirmSend}
                />
            )}
            {logModalReview && (
                <EmployeeEmailLogModal
                    review={logModalReview}
                    onClose={() => setLogModalReview(null)}
                />
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// EMPLOYEE REVIEW CARD - collapsed header + expanded body
// ════════════════════════════════════════════════════════════════════════════
function EmployeeReviewCard({ review, cycle, isExpanded, onToggle, onShowLog, onSend, onPreview }) {
    const fullName = `${review.emp_first_name || ""} ${review.emp_last_name || ""}`.trim() || review.employee_id;
    const initials = `${(review.emp_first_name || "?").charAt(0)}${(review.emp_last_name || "").charAt(0)}`.toUpperCase();
    const managerName = `${review.manager_first_name || ""} ${review.manager_last_name || ""}`.trim() || review.manager_id;
    const status = STATUS_COLORS[review.status] || STATUS_COLORS["Not Started"];

    // Per-step send markers from email_logs. Same source the Cycle Scheduler
    // uses for its sent/✓ pill transformation - keeps the two pages consistent.
    const logs = safeArr(review.email_logs);
    const requestSent = logs.find(l => l.email_type === "request");
    const r1Sent      = logs.find(l => l.email_type === "reminder1");
    const r2Sent      = logs.find(l => l.email_type === "reminder2");

    const canSend = cycle.status === "Active";
    const isInFlight = ["Initiated", "In Progress"].includes(review.status);

    // Cascade: each reminder requires the previous step to have fired.
    // request requires status === "Not Started".
    const canSendRequest = canSend && review.status === "Not Started" && !requestSent;
    const canSendR1 = canSend && isInFlight && !!requestSent && !r1Sent;
    const canSendR2 = canSend && isInFlight && !!r1Sent      && !r2Sent;

    const emailLogs = logs;

    return (
        <div className={`dspr-rm-card dspr-rm-region ${isExpanded ? "expanded" : ""}`}>
            {/* ── Collapsed header ── */}
            <div className="dspr-rm-card-head" onClick={onToggle} role="button" tabIndex={0}>
                <div className="dspr-rm-avatar"
                     style={{ background: profileColorFromName(review.emp_first_name), color: "#fff" }}>
                    {initials}
                </div>
                <div className="dspr-rm-emp-info">
                    {/* Name row - name, emp_id, then status pill INLINE on the
                        left. Matches the dEpr reviews-list layout where status
                        sits with the identity, not on the far right. */}
                    <div className="dspr-rm-emp-name-row">
                        <strong>{fullName}</strong>
                        <span className="dspr-rm-emp-id">{review.employee_id}</span>
                        <span className="dspr-rm-status-pill" style={{ background: status.bg, color: status.fg }}>
                            <span className="dspr-rm-status-dot" style={{ background: status.fg }} />
                            {review.status}
                        </span>
                    </div>
                    <div className="dspr-rm-emp-sub">
                        {review.job_position || "-"} <span className="dspr-rm-sep">·</span> {review.emp_mail_id}
                    </div>
                </div>
                {/* Right side is now intentionally just the chevron - no duplicate status pill */}
                <span className="dspr-rm-chevron">{isExpanded ? "▲" : "▼"}</span>
            </div>

            {/* ── Expanded body ── */}
            {/* Reuses Cycle Scheduler's manager-block classes (.dspr-mgr-block,
                .dspr-mgr-top, .dspr-mgr-identity, .dspr-mgr-right, ...) so the
                visual style is identical to the Cycle Scheduler page. */}
            {isExpanded && (
                <div className="dspr-rm-card-body">
                    <div className="dspr-rm-section-label">REPORTING MANAGER</div>
                    <div className="dspr-mgr-block">
                        <div className="dspr-mgr-top">
                            {/* Left: manager identity */}
                            <div className="dspr-mgr-identity">
                                <div className="dspr-mgr-name-row">
                                    <span className="dspr-mgr-dot" />
                                    <strong>{managerName}</strong>
                                    <span className="dspr-mgr-id">{review.manager_id}</span>
                                </div>
                                {review.manager_email && (
                                    <div className="dspr-mgr-email">{review.manager_email}</div>
                                )}
                            </div>

                            {/* Right: eye + Request Review (or ✓ Requested pill),
                                then stacked Send Reminder 1/2/3 (or their ✓ pills) */}
                            <div className="dspr-mgr-right">
                                <div className="dspr-mgr-right-top">
                                    {requestSent ? (
                                        <span className="dspr-action-done">
                                            ✓ Requested · {fmtDateTimeShort(requestSent.sent_at)}
                                        </span>
                                    ) : (
                                        <>
                                            <button type="button" className="dspr-eye-btn"
                                                onClick={() => onPreview(review, "request")}
                                                disabled={!canSendRequest}
                                                title="Preview Request email">
                                                <FaRegEye />
                                            </button>
                                            <button type="button" className="dspr-batch-btn dspr-batch-req"
                                                disabled={!canSendRequest}
                                                onClick={() => onSend(review, "request")}>
                                                Request Review
                                            </button>
                                        </>
                                    )}
                                </div>

                                <div className="dspr-mgr-reminders">
                                    {[
                                        { n: 1, sent: r1Sent, enabled: canSendR1 },
                                        { n: 2, sent: r2Sent, enabled: canSendR2 },
                                        // R3 retired
                                    ].map(({ n, sent, enabled }) => {
                                        if (sent) {
                                            return (
                                                <span key={n} className="dspr-action-done">
                                                    ✓ Reminder {n} · {fmtDateTimeShort(sent.sent_at)}
                                                </span>
                                            );
                                        }
                                        // Eye preview alongside each Send button - same UX as the
                                        // Request Review row above. HR can preview the reminder
                                        // body (with the per-type escalating deadline) before
                                        // committing the send.
                                        return (
                                            <div key={n} className="dspr-mgr-reminder-row">
                                                <button type="button" className="dspr-eye-btn"
                                                    onClick={() => onPreview(review, `reminder${n}`)}
                                                    disabled={!enabled}
                                                    title={`Preview Reminder ${n} email`}>
                                                    <FaRegEye />
                                                </button>
                                                <button type="button"
                                                    className="dspr-batch-btn dspr-batch-reminder"
                                                    disabled={!enabled}
                                                    onClick={() => onSend(review, `reminder${n}`)}>
                                                    Send Reminder {n}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Small Log link at the bottom of the right column -
                                    opens the EmployeeEmailLogModal */}
                                <button type="button" className="dspr-rm-log-link" onClick={onShowLog}>
                                    📋 Log ({emailLogs.length})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// PREVIEW MODAL - light-weight reuse of the Cycle Scheduler modal
// We re-implement here rather than import to avoid a circular import. Same
// underlying behaviour: fetches POST /api/dspr/email-templates/preview, renders
// the html in an iframe, calls onDirectSend(batch) when the user confirms.
// ════════════════════════════════════════════════════════════════════════════
const ACT_TO_TEMPLATE_TYPE = {
    request:   "request_review",
    reminder1: "reminder_1",
    reminder2: "reminder_2",
};

function BatchPreviewModal({ batch, onClose, onDirectSend }) {
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [recipientsOpen, setRecipientsOpen] = useState(false);
    const [sending, setSending] = useState(false);

    const type = ACT_TO_TEMPLATE_TYPE[batch.act];
    const N = safeArr(batch.employees).length;

    useEffect(() => {
        (async () => {
            setLoading(true); setError(null);
            try {
                // Pre-fill from the actual recipient when known. Review Mgmt
                // always previews a SINGLE specific employee - so show their
                // real name + designation, not a placeholder. (When multiple
                // employees are in the batch, the first one acts as the
                // representative example - every recipient gets the same
                // body, just with their own name/designation substituted at
                // send time.)
                const firstEmp = safeArr(batch.employees)[0] || {};
                const empName = `${firstEmp.emp_first_name || ""} ${firstEmp.emp_last_name || ""}`.trim();
                // Build the REAL review URL so right-click → "Copy link
                // address" gives a working URL. Button is target="_blank" in
                // the template, so left-click opens in a new tab instead of
                // navigating the preview iframe.
                const realLink = firstEmp.id
                    ? `${window.location.origin}/dspr/review?r=${encodeURIComponent(firstEmp.id)}`
                    : "<Personalized self-review link>";
                const vars = {
                    employee_name: empName || "<Recipient Name>",
                    employee_id:   firstEmp.employee_id || "",
                    job_position:  firstEmp.job_position || "<Designation>",
                    review_link:   realLink,
                };
                const res = await apiFetch("/api/dspr/email-templates/preview", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ type, vars, cycle_id: batch.cycleId }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || "Couldn't load preview");
                setTemplate(data);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSend = async () => {
        if (!onDirectSend) return;
        setSending(true);
        try { await onDirectSend(batch); onClose(); }
        catch { /* error toast already shown by caller */ }
        finally { setSending(false); }
    };

    return (
        <div className="dspr-modal-overlay">
            <div className="dspr-modal dspr-preview-modal" onClick={e => e.stopPropagation()}>
                <div className="dspr-modal-head dspr-preview-head">
                    <div className="dspr-preview-head-text">
                        <div className="dspr-preview-head-title">
                            Email Preview
                            <span className="dspr-preview-live-pill" title="Preview reads from saved template. Cache TTL 30s - if you edited the template just now, wait or restart server for immediate refresh.">⟲ live</span>
                        </div>
                        <div className="dspr-preview-head-subtitle">
                            {batch.label} · {batch.cycleHalfLabel} · {N} Employee{N !== 1 ? "s" : ""}
                        </div>
                    </div>
                    <button className="dspr-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="dspr-modal-body">
                    <div className="dspr-preview-header-card">
                        <div className="dspr-preview-meta-row">
                            <span className="dspr-preview-label">To:</span>
                            <span className="dspr-preview-value">&lt;Recipient Name&gt;</span>
                        </div>
                        <div className="dspr-preview-meta-sub">(1 email per employee, sent separately)</div>
                        <div className="dspr-preview-meta-row">
                            <span className="dspr-preview-label">CC:</span>
                            <span className="dspr-preview-value">
                                {(template?.alwaysCc || ["admin@dolluzcorp.com", "anandthshoban@dolluzcorp.com", "hr@dolluzcorp.com"]).join(", ")}
                            </span>
                        </div>
                        <div className="dspr-preview-meta-row">
                            <span className="dspr-preview-label">Sub:</span>
                            <span className="dspr-preview-value dspr-preview-subject-text">
                                {loading ? "Loading…" : error ? "-" : template?.subject || "-"}
                            </span>
                        </div>
                    </div>

                    <div className="dspr-preview-iframe-wrap">
                        {loading ? (
                            <div className="dspr-empty" style={{ padding: 30 }}>Loading preview…</div>
                        ) : error ? (
                            <div className="dspr-tpl-error">⚠ {error}</div>
                        ) : (
                            <iframe className="dspr-preview-iframe" srcDoc={template.html} title="Email Preview"
                                sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox" />
                        )}
                    </div>

                    <div className="dspr-preview-recipients">
                        <button type="button" className="dspr-preview-rec-toggle"
                            onClick={() => setRecipientsOpen(o => !o)}>
                            <span>{recipientsOpen ? "▲" : "▼"} Recipients ({N} employee{N !== 1 ? "s" : ""} will receive personalized emails)</span>
                        </button>
                        {recipientsOpen && (
                            <table className="dspr-preview-rec-table">
                                <thead><tr><th>Emp ID</th><th>Name</th><th>Email</th><th>Status</th></tr></thead>
                                <tbody>
                                    {N === 0 ? (
                                        <tr><td colSpan={4} className="dspr-empty">No eligible employees in this batch.</td></tr>
                                    ) : safeArr(batch.employees).map(r => {
                                        const status = STATUS_COLORS[r.status] || STATUS_COLORS["Not Started"];
                                        return (
                                            <tr key={r.id}>
                                                <td>{r.employee_id}</td>
                                                <td>{r.emp_first_name} {r.emp_last_name}</td>
                                                <td>{r.emp_mail_id}</td>
                                                <td><span className="dspr-pill" style={{ background: status.bg, color: status.fg }}>{r.status}</span></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="dspr-preview-cc-note">
                        ℹ All outgoing emails automatically include the CC list above.
                    </div>
                </div>

                <div className="dspr-modal-foot">
                    <button className="dspr-btn dspr-btn-link" onClick={onClose} disabled={sending}>Cancel</button>
                    <button className="dspr-btn dspr-btn-primary"
                        onClick={handleSend}
                        disabled={sending || N === 0 || loading || !!error}>
                        {sending
                            ? "Sending…"
                            : (N === 1 && batch.singleEmployeeName
                                ? `Send to ${batch.singleEmployeeName}`
                                : `Send to ${N} Employee${N !== 1 ? "s" : ""}`)}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// EMPLOYEE EMAIL LOG MODAL - opened by the small "Log (N)" link on each card
//
// Layout matches the dEpr reference:
//   • Dark navy header - "Email Log - <Employee Name>" + manager subtitle
//   • SENT section - one green-card row per email_log entry (type + To + when)
//   • PENDING section - small grey pills for reminders that haven't fired
//   • Full-width Close button
// ════════════════════════════════════════════════════════════════════════════
const ALL_REMINDER_TYPES = ["reminder1", "reminder2"];

function EmployeeEmailLogModal({ review, onClose }) {
    const fullName = `${review.emp_first_name || ""} ${review.emp_last_name || ""}`.trim() || review.employee_id;
    const managerName = `${review.manager_first_name || ""} ${review.manager_last_name || ""}`.trim() || review.manager_id;

    // Sort sent emails chronologically (oldest first)
    const sentLogs = safeArr(review.email_logs)
        .slice()
        .sort((a, b) => (a.sent_at || "").localeCompare(b.sent_at || ""));

    // Pending reminders = those not yet in email_logs (only reminders are
    // "pending" - request can't be pending once we're looking at logs for a
    // review that's in flight; confirmation is auto on submit)
    const sentTypes = new Set(sentLogs.map(l => l.email_type));
    const pending = ALL_REMINDER_TYPES.filter(t => !sentTypes.has(t));

    return (
        <div className="dspr-modal-overlay">
            <div className="dspr-modal dspr-emlog-modal" onClick={e => e.stopPropagation()}>
                <div className="dspr-modal-head">
                    <div className="dspr-emlog-head-text">
                        <div className="dspr-emlog-title">Email Log - {fullName}</div>
                        <div className="dspr-emlog-subtitle">{review.manager_id} · {managerName}</div>
                    </div>
                    <button className="dspr-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="dspr-modal-body">
                    <div className="dspr-emlog-section-label">SENT</div>
                    {sentLogs.length === 0 ? (
                        <div className="dspr-empty" style={{ padding: "20px 0", fontSize: 12 }}>
                            No emails sent yet for this employee.
                        </div>
                    ) : (
                        <div className="dspr-emlog-sent-list">
                            {sentLogs.map((l, i) => (
                                <div key={i} className="dspr-emlog-sent-row">
                                    <div className="dspr-emlog-sent-info">
                                        <div className="dspr-emlog-sent-head">
                                            <span className="dspr-emlog-sent-dot" />
                                            <strong>{EMAIL_TYPE_LABEL[l.email_type] || l.email_type}</strong>
                                        </div>
                                        <div className="dspr-emlog-to">To: {fullName}</div>
                                    </div>
                                    <div className="dspr-emlog-when">{fmtDateTimeIST(l.sent_at)}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {pending.length > 0 && (
                        <>
                            <div className="dspr-emlog-section-label dspr-emlog-pending-label">PENDING</div>
                            <div className="dspr-emlog-pending-list">
                                {pending.map(t => (
                                    <span key={t} className="dspr-emlog-pending-pill">
                                        {EMAIL_TYPE_LABEL[t] || t}
                                    </span>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <div className="dspr-modal-foot dspr-emlog-foot">
                    <button type="button" className="dspr-emlog-close-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DSprReviewManagement;
